import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { logger } from '../../utils/logger';
import { en } from '../../translations/en';
import api from '../../utils/api';

interface ConsentState {
  marketingConsent: boolean;
  marketingConsentUpdatedAt: string | null;
}

const MarketingConsentSection: React.FC = () => {
  const [consent, setConsent] = useState<ConsentState>({
    marketingConsent: false,
    marketingConsentUpdatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConsent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users/me/marketing-consent');
      setConsent({
        marketingConsent: response.data.marketingConsent ?? true,
        marketingConsentUpdatedAt: response.data.marketingConsentUpdatedAt ?? null,
      });
    } catch (err) {
      logger.error('Failed to fetch marketing consent', err);
      setError(en.settings.account.marketingConsent.updateError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConsent();
  }, [fetchConsent]);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setSaving(true);
    setError(null);

    try {
      const response = await api.put('/api/users/me/marketing-consent', {
        marketingConsent: newValue,
      });
      setConsent({
        marketingConsent: response.data.marketingConsent,
        marketingConsentUpdatedAt: response.data.marketingConsentUpdatedAt,
      });
    } catch (err) {
      logger.error('Failed to update marketing consent', err);
      setError(en.settings.account.marketingConsent.updateError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        {en.settings.account.marketingConsent.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {en.settings.account.marketingConsent.description}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={consent.marketingConsent}
            onChange={handleToggle}
            disabled={saving}
            data-testid="marketing-consent-toggle"
          />
        }
        label={
          consent.marketingConsent
            ? en.settings.account.marketingConsent.enabled
            : en.settings.account.marketingConsent.disabled
        }
      />
    </Box>
  );
};

export default MarketingConsentSection;
